/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import useRouting from "hooks/useRouting";
import { useEffect, useMemo, useReducer, useState } from "react";
import { getAirportLabel } from "../FlightForm/useAirportSearch";

import {
  checkIfUserApplicationHasErrors,
  mapApplicationErrorsToFields,
  parseApplicationErrors,
} from "Models/BlinkApplication";
import STATES from "Models/UIStates";
import { useOrgData } from "state/OrgDataContext";
import { default as classNames } from "classnames";
import {
  Button,
  DocumentViewer,
  FileInput,
  FontAwesomeIcon,
  Heading,
  HeadlessUploader,
  Input,
  Label,
  Select,
  Stack,
  Text,
} from "components";
import { PREFIX_OPTIONS, OTHER_PREFIX_VALUE } from "constants/prefixes";
import { getUIPrefix } from "utils/prefix";
import Checkbox from "components/Input/Checkbox";
import { LoadingDots } from "components/Loaders";
import SearchableSelect from "components/SearchableSelect/SearchableSelect";

import useCompanion from "hooks/useCompanion";
import useFetcher from "hooks/useFetcher";
import { findAirportById, searchAirport } from "lib/airportApi";
import {
  getAttendees,
  getEventDefaultFlight,
  getFormCategories,
  getFormCategoryFields,
} from "lib/userApi";
import { useRef } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import ROUTES from "routes";
import { useUI } from "state";
import { useUser } from "state/UserContext";
import { PageLayout } from "templates";
import InstructionsPage from "templates/InstructionsModal/Instructions";
import decrypt from "utils/decrypt";
import formatSelectOptions from "utils/formatSelectOptions";
import getBase64Details from "utils/getBase64Details";
import getFormattedDate from "utils/getFormattedDate";
import usePreviousRoute from "utils/usePreviousRoute";
import warning from "utils/warning";
import passportplaceholder from "../../../assets/passportplaceholder.png";
import portraitplaceholderDark from "../../../assets/placeholder-dark.svg";
import portraitplaceholder from "../../../assets/placeholder.svg";
import sectionsData from "./Sections.json";
import useClientDocuments from "./useClientDocuments";
import useSubmitGuestForm from "./useSubmitGuestForm";
import useDynamicFields from "./useDynamicFields";
import FormSection from "./FormSection";
import TermsOfUseAndPolicyModal from "templates/TermsOfUseAndPolicyModal";
import { getEventDefaultCountryCode } from "lib/eventsApi";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

const SpanErrorMessage = ({ error }) => {
  const { t } = useTranslation();
  const Span = ({ children }) => (
    <div className="w-full !mt-0">
      <span
        className="text-sm -text--theme-colors-danger-default absolute"
        data-error
      >
        {children}
      </span>
    </div>
  );
  if (!error || error?.length === 0) return null;
  const message = Array.isArray(error) ? error[0] : error;
  return <Span>{message}</Span>;
};

const FieldSet = ({ children }) => (
  <Stack className="space-y-2.5 relative w-full">{children}</Stack>
);

const FieldRow = ({ children }) => (
  // <div className="flex flex-col md:flex-row space-y-8 md:space-y-s0 md:space-x-2.5 md:space-x-8">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 md:gap-y-s0 md:gap-x-2.5 md:gap-x-8">
    {children}
  </div>
);

const PersonalInfo = ({ isEditing = false }) => {
  const { t } = useTranslation();
  const { isLoading } = useUser();
  const [isReviewing, setIsReviewing] = useState(false);
  const { userId, eventId } = useParams();
  const [companion] = useCompanion(userId, {
    stale: false,
  });
  const isApplicationHasErrors = checkIfUserApplicationHasErrors(companion);
  const goBack = usePreviousRoute(
    isEditing ? ROUTES.onboard.registration : ROUTES.dashboard.base,
  );
  const { openModal, closeModal } = useUI();
  const { data: flightOptions } = useFetcher(() => getAttendees(eventId), {
    key: `attendees-${eventId}`,
    enabled: !!eventId,
  });
  const { event: EVENT } = useEnvs();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const [userFormCategory, setUserFormCategory] = useState();
  const [categories, setCategories] = useState();

  const [formErrors, setFormErrors] = useState({
    id_type: "",
    nationality: "",
    passportType: "",
    passportNumber: "",
    QIDNumber: "",
    issuingAuthority: "",
    expireDate: "",
    firstName: "",
    lastName: "",
    firstNameInNative: "",
    lastNameInNative: "",
    middleName: "",
    middleNameInNative: "",
    gender: "",
    dateOfBirth: "",
    title: "",
    company: "",
    email: "",
    phoneNumber: "",
    countryCode: "",
    emergencyPhoneNumber: "",
    emergencyCountryCode: "",
    emergencyContactFullName: "",
    outbound_arrival_airport_code: "",
    return_arrival_airport_code: "",
    outbound_departure_airport_code: "",
    return_departure_airport_code: "",
    relationship: "",
    portrait: "",
    passport: "",
    assistantEmail: "",
    assistantPhoneNumber: "",
    assistantCountryCode: "",
    prefix: "",
    customPrefix: "",
  });

  const [id_type, setIdType] = useState("");
  const [nationality, setNationality] = useState("");
  const [passportType, setPassportType] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [QIDNumber, setQIDNumber] = useState("");
  const [issuingAuthority, setIssuingAuthority] = useState("");
  const [expireDate, setExpireDate] = useState("2024-01-01");
  const [firstName, setFirstName] = useState("");
  const [firstNameInNative, setFirstNameInNative] = useState("");

  const [lastName, setLastName] = useState("");
  const [lastNameInNative, setLastNameInNative] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [middleNameInNative, setMiddleNameInNative] = useState("");
  const [title, setTitle] = useState("");
  const [prefix, setPrefix] = useState("");
  const [customPrefix, setCustomPrefix] = useState("");
  const [company, setCompany] = useState("");
  const [professionalDomain, setProfessionalDomain] = useState({
    government: false,
    media: false,
    business: false,
    other: false,
  });
  // const handleFamilyNameChange = (e) => {
  //   const { name, value } = e.target;
  //   setFamilyNames((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  //   setFormErrors((prev) => {
  //     return { ...prev, [name]: "" };
  //   });
  // };
  const [gender, setGender] = useState("");
  const [relationship, setRelationship] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("1980-01-01");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [defaultCountryCode, setDefaultCountryCode] = useState("");
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [email, setEmail] = useState("");
  const [emergencyPhoneNumber, setEmergencyPhoneNumber] = useState("");
  const [emergencyContactFullName, setEmergencyContactFullName] = useState("");
  const [emergencyCountryCode, setEmergencyCountryCode] =
    useState(defaultCountryCode);
  const { shouldEnterApplicationForm } = useRouting();
  const [conditions, setConditions] = useState({
    willBookFlight: false,
    instructions: false,
  });
  const [flightDetails, setFlightDetails] = useState({
    outbound_departure_airport_code: "",
    outbound_arrival_airport_code: "",
    return_departure_airport_code: "",
    return_arrival_airport_code: "",
  });
  const [flightType, setFlightType] = useState();
  const [flightOptionID, setFlightOptionID] = useState("");

  const preferredTimes = useMemo(
    () => [
      {
        id: 1,
        value: "No Preferences",
        label: t("medicalConditions.specialConcernsNoPreference"),
      },
      { id: 2, value: "Morning", label: t("common.time.morning") },
      { id: 3, value: "Afternoon", label: t("common.time.afternoon") },
      { id: 4, value: "Evening", label: t("common.time.evening") },
      { id: 5, value: "Night", label: t("common.time.night") },
    ],
    [t],
  );

  const [selectedPref, setSelectedPref] = useState({
    outboundSelectedPrefTime: preferredTimes[0].value,
    returnSelectedPrefTime: preferredTimes[0].value,
    outboundSelectedPrefDate: "",
    returnSelectedPrefDate: "",
  });

  const [privacyCheck, setPrivacyCheck] = useState(false);
  const [flightNotes, setFlightNotes] = useState("");
  const [passportsubmittion, setPassportsubmittion] = useState(false);
  const [validVisa, setValidVisa] = useState(false);
  const [passportValid, setPassportValid] = useState(false);
  const [bio, setBio] = useState("");
  const [showFileInput, setShowFileInput] = useState(false);
  const [assistantEmail, setAssistantEmail] = useState("");
  const [assistantPhoneNumber, setAssistantPhoneNumber] = useState("");
  const [assistantCountryCode, setAssistantCountryCode] =
    useState(defaultCountryCode);
  const [documents, setClientDocuments] = useClientDocuments();
  const { passport, portrait } = documents;
  const [tripOptions, setTripOption] = useState({
    outbound_arrival_airport_code: {
      options: [],
      isLoading: false,
    },
    outbound_departure_airport_code: {
      options: [],
      isLoading: false,
    },
    return_arrival_airport_code: {
      options: [],
      isLoading: false,
    },
    return_departure_airport_code: {
      options: [],
      isLoading: false,
    },
  });
  const [sections, setSections] = useState(sectionsData);
  const [isFetching, setIsFetching] = useState(true);

  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const scrollToError = () => {
    scrollTimeoutRef.current = setTimeout(() => {
      const el = document.querySelector("[data-error]");

      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  useEffect(() => {
    const eventFlightData = async () => {
      if (flightOptionID !== 1) return;
      let data = await getEventDefaultFlight(eventId);
      // When editing an existing companion, avoid overwriting existing flight details with undefined defaults
      setFlightDetails((prev) => ({
        outbound_departure_airport_code:
          data?.outbound_airport ?? prev.outbound_departure_airport_code ?? "",
        outbound_arrival_airport_code:
          data?.return_airport ?? prev.outbound_arrival_airport_code ?? "",
        return_departure_airport_code:
          data?.return_airport ?? prev.return_departure_airport_code ?? "",
        return_arrival_airport_code:
          data?.outbound_airport ?? prev.return_arrival_airport_code ?? "",
      }));
    };
    eventFlightData();
  }, [flightOptionID, userFormCategory]);

  useEffect(() => {
    if (userId === "new" || !companion) return;
    // navigate out if he already submitted the form
    shouldEnterApplicationForm(companion);
    // -------------------------------
    setFormErrors({
      ...formErrors,
      ...mapApplicationErrorsToFields(companion),
    });
    const documentNumber = decrypt(companion?.document_number);
    setPhoneNumber(companion.contact_number);
    setEmergencyPhoneNumber(companion.emergency_contact_phone_number);
    setCountryCode(companion.country_code || defaultCountryCode);
    setEmergencyCountryCode(
      companion.emergency_country_code || defaultCountryCode,
    );
    setIdType(companion?.id_type || "");
    setNationality(companion?.nationality || "");
    setPassportType(companion?.document_type || "");
    setQIDNumber(documentNumber || "");
    setPassportNumber(documentNumber || "");
    setIssuingAuthority(companion?.document_issuing_authority || "");
    setExpireDate(companion?.document_expiration_date || "");
    const isPrefixEnabled = !!sections?.["personal_info"]?.fields?.prefix;
    if (!isPrefixEnabled) {
      setPrefix("");
      setCustomPrefix("");
    } else {
      const { selected, custom } = getUIPrefix(
        companion?.prefix,
        PREFIX_OPTIONS,
      );
      setPrefix(selected);
      setCustomPrefix(custom);
    }
    setFirstName(companion?.first_name || "");
    setFirstNameInNative(companion?.native_first_name || "");
    setLastName(companion?.last_name || "");
    setLastNameInNative(companion?.native_last_name || "");
    setMiddleNameInNative(companion?.native_middle_name || "");
    setMiddleName(companion?.middle_name || "");
    setGender(companion?.gender || "");
    setDateOfBirth(companion?.birth_date || "");
    setRelationship(companion?.relationship || "");
    setConditions((prev) => ({
      ...prev,
      willBookFlight: companion?.self_book_flight,
    }));

    setFlightNotes(companion?.flight_notes || "");
    // If flight type is Standard (1) or Business (3) and user is self-booking, change flight type Attendee-Driven Booking (2)
    const attendeeDriveFlightBooking =
      (companion?.flight_type === 1 || companion?.flight_type === 3) &&
      companion?.self_book_flight
        ? 2
        : companion?.flight_type;
    setFlightType(attendeeDriveFlightBooking);
    setFlightDetails({
      outbound_departure_airport_code: companion?.outbound_airport || "",
      outbound_arrival_airport_code: companion?.return_airport || "",
      return_departure_airport_code: companion?.return_airport || "",
      return_arrival_airport_code: companion?.outbound_airport || "",
    });
    setBio(companion?.bio || "");
    setAssistantCountryCode(
      companion?.assistant_country_code || defaultCountryCode,
    );
    setAssistantEmail(companion?.assistant_email || "");
    setAssistantPhoneNumber(companion?.assistant_contact_number || "");

    const mapping = {
      1: "government",
      2: "media",
      3: "business",
      4: "other",
    };

    setProfessionalDomain({
      government: companion?.professional_domain == 1 || false,
      media: companion?.professional_domain == 2 || false,
      business: companion?.professional_domain == 3 || false,
      other: companion?.professional_domain == 4 || false,
    });

    setTitle(companion?.title || "");
    setCompany(companion?.organization || "");
    setPassportValid(companion?.is_passport_valid || false);
    setPassportsubmittion(companion?.is_passport_submitted_to_admins || false);
    setValidVisa(companion?.is_valid_visa || false);
    const getAirports = async () => {
      const {
        outbound_departure_airport_code,
        outbound_arrival_airport_code,
        return_departure_airport_code,
        return_arrival_airport_code,
      } = companion;
      const newDetails = { ...flightDetails };
      try {
        if (
          outbound_departure_airport_code &&
          outbound_departure_airport_code.length > 0
        ) {
          const api = await findAirportById(
            outbound_departure_airport_code,
            eventId,
          );
          const [firstFrom] = api.data;
          newDetails.outbound_departure_airport_code = firstFrom?.code;
          newDetails.outbound_departure_airport_name = firstFrom?.name;
          searchAirports(null, "outbound_departure_airport_code", [firstFrom]);
        }
      } catch (err) {
        setTripOption({
          ...tripOptions,
          outbound_departure_airport_code: {
            options: [],
            isLoading: false,
          },
        });
        warning(err);
      }
      try {
        if (
          outbound_arrival_airport_code &&
          outbound_arrival_airport_code.length > 0
        ) {
          const api = await findAirportById(
            outbound_arrival_airport_code,
            eventId,
          );
          const [firstTo] = api.data;
          newDetails.outbound_arrival_airport_code = firstTo?.code;
          newDetails.outbound_arrival_airport_name = firstTo?.name;
          searchAirports(null, "outbound_arrival_airport_code", [firstTo]);
        }
      } catch (err) {
        setTripOption({
          ...tripOptions,
          outbound_arrival_airport_code: {
            options: [],
            isLoading: false,
          },
        });
        warning(err);
      }
      try {
        if (
          return_departure_airport_code &&
          return_departure_airport_code.length > 0
        ) {
          const api = await findAirportById(
            return_departure_airport_code,
            eventId,
          );
          const [secondFrom] = api.data;
          newDetails.return_departure_airport_code = secondFrom?.code;
          newDetails.return_departure_airport_name = secondFrom?.name;
          searchAirports(null, "return_departure_airport_code", [secondFrom]);
        }
      } catch (err) {
        setTripOption({
          ...tripOptions,
          return_departure_airport_code: {
            options: [],
            isLoading: false,
          },
        });
        warning(err);
      }
      try {
        if (
          return_arrival_airport_code &&
          return_arrival_airport_code.length > 0
        ) {
          const api = await findAirportById(
            return_arrival_airport_code,
            eventId,
          );
          const [secondTo] = api.data;
          newDetails.return_arrival_airport_code = secondTo?.code;
          newDetails.return_arrival_airport_name = secondTo?.name;
          searchAirports(null, "return_arrival_airport_code", [secondTo]);
        }
      } catch (err) {
        setTripOption({
          ...tripOptions,
          return_arrival_airport_code: {
            options: [],
            isLoading: false,
          },
        });
        warning(err);
      }
      setFlightDetails(newDetails);
    };
    getAirports();
    setSelectedPref({
      outboundSelectedPrefTime:
        companion?.outbound_flight_preferred_day_period || "",
      returnSelectedPrefTime:
        companion?.return_flight_preferred_day_period || "",
      outboundSelectedPrefDate: companion?.outbound_flight_preferred_date || "",
      returnSelectedPrefDate: companion?.return_flight_preferred_date || "",
    });

    setEmail(companion?.email || "");
    setEmergencyContactFullName(companion?.emergency_contact_full_name || "");

    const portraitDetails = getBase64Details(companion?.personal_image);
    const passportDetails = getBase64Details(companion?.passport_image);
    const hasExistingPassport =
      Boolean(passportDetails.initial) ||
      Boolean(companion?.is_passport_submitted_to_admins);

    const portraitDocument = {
      state: portraitDetails.initial ? STATES.success : STATES.initial,
      value: portraitDetails.initial,
      isLoading: false,
      preview: portraitDetails.src,
      extension: portraitDetails.extension,
    };
    const passportDocument = {
      state: hasExistingPassport ? STATES.success : STATES.initial,
      value: passportDetails.initial || companion?.passport_image || "",
      isLoading: false,
      preview: passportDetails.src,
      extension: passportDetails.extension,
    };
    const documents = {
      passport: passportDocument,
      portrait: portraitDocument,
    };
    setClientDocuments(documents);
  }, [companion, sections]);

  const formData = {
    id_type,
    passport,
    portrait,
    expireDate,
    gender,
    dateOfBirth,
    emergencyPhoneNumber,
    emergencyContactFullName,
    firstName,
    lastName,
    middleName,
    phoneNumber,
    email,
    firstNameInNative,
    lastNameInNative,
    middleNameInNative,
    QIDNumber,
    passportType,
    passportNumber,
    issuingAuthority,
    nationality,
    documents,
    countryCode,
    emergencyCountryCode,
    flightDetails,
    relationship,
    flightNotes,
    professionalDomain,
    validVisa,
    passportsubmittion,
    passportValid,
    flightType,
    title,
    company,
    bio,
    assistantEmail,
    assistantPhoneNumber,
    assistantCountryCode,
    selectedPref,
    prefix,
    customPrefix,
  };

  const fetchCategory = async (id) => {
    setIsFetching(true);
    setConditions((prev) => ({ ...prev, instructions: false }));
    const category = await getFormCategoryFields(id, eventId);
    setSections(category.data.configuration_obj);
    setIsFetching(false);
  };

  const [SelectedCatId, setSelectedCatId] = useState();
  const attributes = {
    title: t("onboarding.title"),
    hasGoBack: true,
    goBackMethod: goBack,
    hasPolicy: true,
  };
  if (!isEditing) {
    attributes.note = t("onboarding.description");
  }
  const handleOnChange = (handler, value, key) => {
    handler(value);
    setFormErrors((prev) => {
      return { ...prev, [key]: "" };
    });
  };

  const handleCustomPrefixChange = (value) => {
    setCustomPrefix(value);
    setFormErrors((prev) => {
      return { ...prev, customPrefix: "" };
    });
  };

  const handleDynamicFieldChange = (fieldKey, value) => {
    // Special handling for prefix field to sync with local state
    if (fieldKey === "prefix" || fieldKey.includes("prefix")) {
      setPrefix(value);
      setFormErrors((prev) => {
        return { ...prev, prefix: "" };
      });
      // Clear custom prefix if not "Other"
      if (value !== OTHER_PREFIX_VALUE) {
        setCustomPrefix("");
        setFormErrors((prev) => {
          return { ...prev, customPrefix: "" };
        });
      }
    }

    // Auto-select authority when nationality is selected
    if (fieldKey === "nationality") {
      updateDynamicField(fieldKey, value);

      const currentIdType = dynamicFieldValues["id_type"] || id_type;
      if (+currentIdType === 3) {
        updateDynamicField("document_issuing_authority", value);
      }
      return;
    }

    // Handle id_type changes - if changed to passport and nationality exists, auto-select authority
    if (fieldKey === "id_type") {
      updateDynamicField(fieldKey, value);

      // If changed to passport (3) and nationality exists, auto-select authority
      if (+value === 3) {
        const currentNationality =
          dynamicFieldValues["nationality"] || nationality;
        if (currentNationality) {
          updateDynamicField("document_issuing_authority", currentNationality);
        }
      }
      return;
    }

    updateDynamicField(fieldKey, value);
  };

  const searchAirports = async (search, option_name, passedData = null) => {
    try {
      setTripOption((prev) => ({
        ...prev,
        [option_name]: { ...prev[option_name], isLoading: true },
      }));
      let data = passedData;
      if (!passedData) {
        const { ok, data: payload } = await searchAirport(search, eventId);
        if (ok === false) {
          setTripOption((prev) => ({
            ...prev,
            [option_name]: { ...prev[option_name], isLoading: false },
          }));
          return;
        }
        data = payload;
      }
      const options = formatSelectOptions(data, null, false, (option) => ({
        label: `${option.name} ${t("common.labels.airport")} (${option.code})`,
        value: option.code,
        id: option.code,
      }));
      setTripOption((prev) => ({
        ...prev,
        [option_name]: { options, isLoading: false },
      }));
    } catch (err) {
      setTripOption((prev) => ({
        ...prev,
        [option_name]: { ...prev[option_name], isLoading: false },
      }));
      warning(err);
    }
  };

  const imagePresent = sections["documents"]?.fields.image;
  const passportImagePresent = sections["documents"]?.fields.passport_image;

  const imageNotSuccess = documents.portrait?.state !== "success";
  const passportImageNotSuccess = documents.passport?.state !== "success";

  const uploadRequired = !!(imagePresent || passportImagePresent);

  const isFormDisabled = !uploadRequired
    ? false
    : (imagePresent && imageNotSuccess) ||
      (passportImagePresent && passportImageNotSuccess);

  useEffect(() => {
    if (!sections) return;
    const hasDocumentUploadFields =
      !!sections["documents"]?.fields.image ||
      !!sections["documents"]?.fields.passport_image;
    if (hasDocumentUploadFields) {
      setConditions((prev) => ({
        ...prev,
        instructions: false,
      }));
    } else {
      setConditions((prev) => ({
        ...prev,
        instructions: true,
      }));
    }
  }, [sections]);

  const isCompanion = companion?.role === "companion" || userId === "new";

  // Dynamic fields hook - placed after isCompanion is defined
  const {
    dynamicFieldValues,
    dynamicFieldErrors,
    dynamicFields,
    hasDynamicFields,
    updateDynamicField,
    setDynamicFieldError,
    validateDynamicFields,
    getFieldNames,
    getDynamicFieldKeys,
  } = useDynamicFields(sections, companion, isCompanion);

  useEffect(() => {
    if (!companion) return;
    if (!dynamicFields || Object.keys(dynamicFields).length === 0) return;
    const appErrors = parseApplicationErrors(companion);
    if (!appErrors) return;
    const erroredKeys = [];
    Object.entries(appErrors).forEach(([key, value]) => {
      if (!dynamicFields[key]) return;
      const comment =
        value && typeof value === "object" ? value.comment || "" : "";
      if (comment) {
        setDynamicFieldError(key, comment);
        erroredKeys.push(key);
      }
    });
    // Scroll to first errored field
    if (erroredKeys.length > 0) {
      const firstKey = erroredKeys[0];
      setTimeout(() => {
        const el =
          document.getElementById(firstKey) ||
          document.getElementsByName(firstKey)[0];
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
    }
  }, [companion, dynamicFields]);

  // Now we can use the functions from the dynamic fields hook
  const allFieldKeys = [
    ...Object.values(sections).flatMap((section) =>
      section.fields ? Object.keys(section.fields) : [],
    ),
    ...getFieldNames(),
  ];

  const { handleOnSubmit, validateForm } = useSubmitGuestForm(
    formData,
    formErrors,
    setFormErrors,
    isEditing,
    conditions,
    allFieldKeys,
    SelectedCatId,
    validateDynamicFields,
    getDynamicFieldKeys,
    dynamicFieldErrors,
    customPrefix,
    flightOptionID,
    dynamicFields,
  );

  const onDisabledClick = () =>
    isFormDisabled && toast(t("guestInfoPage.uploadDocsFirst"));
  const passportImageRef = useRef(null);

  const handleProfessionalDomainClick = (key) => {
    setProfessionalDomain((prev) => {
      return Object.keys(prev).reduce((acc, currKey) => {
        return { ...acc, [currKey]: key === currKey };
      }, {});
    });
    setUserFormCategory(key);
  };

  useEffect(() => {
    if (countryCode === "") {
      setCountryCode(EVENT_CONTENT.defaultCountryCode);
    }
  }, [countryCode]);

  useEffect(() => {
    setCountryCode(defaultCountryCode);
    setEmergencyCountryCode(defaultCountryCode);
    setAssistantCountryCode(defaultCountryCode);
  }, [defaultCountryCode]);

  // Sync prefix value from parent state to dynamic fields
  useEffect(() => {
    if (sections["personal_info"]?.fields?.prefix) {
      // Only update if the current dynamic field value is different
      if (dynamicFieldValues.prefix !== prefix) {
        updateDynamicField("prefix", prefix);
      }
    }
  }, [prefix, sections, updateDynamicField, dynamicFieldValues.prefix]);

  const birthDateFormatted = getFormattedDate(dateOfBirth);
  const expirationDateFormatted = getFormattedDate(expireDate);

  const getFormsCategories = async () => {
    const response = await getFormCategories(eventId);
    const categoriesData = response.data;

    if (categoriesData.length > 1) {
      setCategories(categoriesData);
      setIsFetching(false);
      if (companion?.registration_form_category_id) {
        const matchingCategory = categoriesData?.find(
          (category) =>
            category.id === companion?.registration_form_category_id,
        );
        if (matchingCategory) {
          setSelectedProfessionalDomain(matchingCategory.professional_domain);
          setSelectedCatId(matchingCategory.id);
          fetchCategory(matchingCategory.id);
        }
        return;
      }
      setSelectedProfessionalDomain(categoriesData[0].professional_domain);
      setSelectedCatId(categoriesData[0].id);
      fetchCategory(categoriesData[0].id);
    } else if (categoriesData.length === 1) {
      fetchCategory(categoriesData[0].id);
      setSelectedCatId(categoriesData[0].id);
    } else {
      setSections(sectionsData);
      setSelectedCatId(0);
      setIsFetching(false);
      setUserFormCategory("");
    }
    setUserFormCategory(categoriesData[0].professional_domain);
  };
  useEffect(() => {
    getFormsCategories();
  }, [companion?.registration_form_category_id]);

  const [selectedProfessionalDomain, setSelectedProfessionalDomain] =
    useState();

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const resizeImageIfNeeded = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        // Create a new image using the native Image constructor
        const img = new window.Image(); // Use 'window.Image' to explicitly refer to the global Image class
        img.src = e.target.result;

        img.onload = () => {
          const maxWidth = 800;

          // Check if the image width is greater than the max width
          if (img.width > maxWidth) {
            const height = img.height * (maxWidth / img.width);
            const canvas = document.createElement("canvas");
            canvas.width = maxWidth;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, maxWidth, height);
            canvas.toBlob((blob) => {
              resolve(new File([blob], file.name, { type: file.type }));
            }, file.type);
          } else {
            // If the image is already smaller, resolve with the original file
            resolve(file);
          }
        };
        img.onerror = reject;
      };
    });
  };

  const { colorScheme } = useUI();

  useEffect(() => {
    const flightOption =
      flightOptions?.user_details?.registration_form_data?.flight_type;
    setFlightOptionID(flightOption);

    // if there is no user data assign flight type from flightOptions (getAttendees)
    if (!companion) {
      setFlightType(flightOption);
    }
  }, [flightOptions]);

  useEffect(() => {
    if (!isReviewing) {
      setPrivacyCheck(false);
    }
  }, [isReviewing]);

  useEffect(() => {
    const fetchCountryCode = async () => {
      try {
        const { data } = await getEventDefaultCountryCode(eventId);
        setDefaultCountryCode(data?.country_code);
      } catch (error) {
        console.error("Failed to fetch country code: ", error);
      }
    };

    fetchCountryCode();
  }, [eventId]);

  /* Documents Section - Dynamic Fields Only */
  const documentFields = useMemo(() => {
    if (!sections["documents"]?.fields) return {};

    return Object.values(sections["documents"].fields)
      .filter(
        (field) => field.key !== "image" && field.key !== "passport_image",
      )
      .reduce((acc, field) => ({ ...acc, [field.key]: field }), {});
  }, [sections]);

  const documentsSection = useMemo(
    () => ({
      ...sections,
      documents: {
        ...sections["documents"],
        fields: documentFields,
      },
    }),
    [sections, documentFields],
  );

  /**
   * Updates the flight type based on the user's flight booking choice.
   * If selfBookFlight is true, it sets the flight type to Attendee-Driven Booking (2).
   * Otherwise, it reverts to the initial flightOptionID.
   */
  const handleAttendeeDriveFlightBooking = (selfBookFlight) => {
    setFlightType(selfBookFlight ? 2 : flightOptionID);
  };

  return (
    <PageLayout {...attributes}>
      <Stack
        component="form"
        className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 md:px-[54px] mt-5"
      >
        {!isFetching ? (
          <Stack component="section">
            <Stack className="gap-y-4 mb-10">
              <>
                <Stack>
                  {!isReviewing && categories?.length > 1 && (
                    <Heading
                      as="h2"
                      className="-text--theme-colors-text-title-brand"
                    >
                      {t("guestInfoPage.categoryLabel")}
                    </Heading>
                  )}
                  {!isReviewing && categories?.length > 1 && (
                    <Text className="-text--component-colors-field-default-description">
                      {t("guestInfoPage.selectTheProfessionalDomainMSG")}
                    </Text>
                  )}
                </Stack>
                {!isReviewing && categories?.length > 1 && (
                  <div className="gap-y-2.5 grid md:grid-cols-2 mb-3 md:gap-x-2.5">
                    {categories?.map((category) => (
                      <div
                        onClick={() => {
                          if (isApplicationHasErrors) return;
                          handleProfessionalDomainClick(
                            category.professional_domain,
                          );
                          setSelectedProfessionalDomain(
                            category.professional_domain,
                          );
                          setSelectedCatId(category.id);
                          fetchCategory(category.id);
                        }}
                        aria-disabled={isApplicationHasErrors}
                        key={category.id}
                        aria-selected={
                          selectedProfessionalDomain ===
                          category.professional_domain
                        }
                        className={classNames(
                          {
                            "select-none pointer-events-none":
                              isApplicationHasErrors,
                          },
                          "aria-disabled:-bg--theme-colors-surface-neutral-level03 aria-disabled:-text--component-colors-gd-option-selection-card-disabled-high aria-disabled:-border--component-colors-gd-option-selection-card-disabled-high",
                          "aria-selected:-bg--component-colors-gd-option-selection-card-selected-bg aria-selected:-border--component-colors-gd-option-selection-card-selected-border  aria-selected:-text--component-colors-gd-option-selection-card-selected-high",
                          "px-5 py-2.5 border group-text--component-colors-gd-option-selection-card-default-high -bg--component-colors-gd-option-selection-card-default-bg cursor-pointer hover:-bg--component-colors-gd-option-selection-card-hover-bg -rounded-form-category",
                        )}
                      >
                        <div className="flex gap-x-4">
                          <div
                            aria-disabled={isApplicationHasErrors}
                            className=" w-4 h-4 border p-1  aria-disabled:-border--component-colors-gd-option-selection-card-disabled-high -border--component-colors-gd-option-selection-card-default-high group-aria-selected:-border--theme-colors-action-secondary-default-fg rounded-full self-center"
                          >
                            {selectedProfessionalDomain ===
                              category.professional_domain && (
                              <div className=" w-full h-full -bg--theme-colors-action-secondary-default-fg rounded-full"></div>
                            )}
                          </div>
                          <Text className="self-center font-normal capitalize -text--component-colors-gd-option-selection-card-default-high">
                            {category.professional_domain}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isReviewing && userFormCategory && (
                  <Stack className="gap-y-2.5">
                    <Heading
                      as="h2"
                      className="-text--theme-colors-text-title-brand"
                    >
                      {t("guestInfoPage.categoryLabel")}
                    </Heading>
                    <Text className="-text--theme-colors-text-body text-base">
                      {userFormCategory}
                    </Text>
                  </Stack>
                )}
              </>
            </Stack>
            <>
              {sections["documents"]?.fields.image ||
              sections["documents"]?.fields.passport_image ? (
                <Stack className="gap-y-2.5">
                  <Heading
                    as="h2"
                    className="-text--theme-colors-text-title-brand mb-5"
                  >
                    {t("guestInfoPage.documentsLabel")}
                  </Heading>
                  {!isReviewing && EVENT !== "SPORTICO" && (
                    <div className="flex gap-x-2.5 items-start mb-3">
                      <Label
                        htmlFor="instructions_toggle"
                        className="-text--component-colors-check-radio-inactive-text text-sm font-normal flex sm:items-center items-start gap-x-2 leading-relaxed w-full"
                      >
                        <Checkbox
                          name="instructions_toggle"
                          id="instructions_toggle"
                          checked={conditions.instructions}
                          onChange={(e) => {
                            setConditions((prev) => ({
                              ...prev,
                              instructions: e.target.checked,
                            }));
                          }}
                          className="flex-shrink-0"
                        />

                        <span className="flex-1">
                          {t("guestInfoPage.reviewInstructionsIntro")}{" "}
                          <span
                            className="underline cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openModal({
                                content: <InstructionsPage />,
                              });
                            }}
                          >
                            {t("guestInfoPage.reviewInstructionsTitle")}
                          </span>
                        </span>
                      </Label>
                    </div>
                  )}
                </Stack>
              ) : null}
              {isReviewing &&
              (portrait.preview ||
                sections["documents"]?.fields.passport_image) ? (
                <>
                  <div className="flex flex-col md:flex-row justify-start items-start md:gap-x-2.5 space-y-2.5 md:space-y-s0">
                    {portrait.preview && (
                      <div className="flex flex-col gap-y-2">
                        <Label isPreview={isReviewing} required={!isReviewing}>
                          {t("guestInfoPage.portraitTitle")}
                        </Label>
                        <DocumentViewer
                          src={portrait.preview}
                          extension={portrait.extension}
                          alt={
                            portrait.preview
                              ? t("guestInfoPage.portraitAlt")
                              : t("guestInfoPage.portraitPlaceholderAlt")
                          }
                          className="w-52 h-52 object-cover -bg--theme-colors-surface-neutral-level03 -rounded-image-viewer"
                        />
                      </div>
                    )}
                    {sections["documents"]?.fields.passport_image && (
                      <div className="flex flex-col gap-y-2">
                        <Label isPreview={isReviewing}>
                          {t("guestInfoPage.passportTitle")}
                        </Label>
                        <DocumentViewer
                          src={passport.preview || passportplaceholder}
                          extension={passport.extension}
                          alt={
                            passport.preview
                              ? t("guestInfoPage.passportAlt")
                              : t("guestInfoPage.passportPlaceholderAlt")
                          }
                          className="w-52 h-52 object-cover -bg--theme-colors-surface-neutral-level03 -rounded-image-viewer"
                        />
                      </div>
                    )}
                  </div>
                  <div className="h-10"></div>
                </>
              ) : (
                <>
                  {sections["documents"]?.fields.image && (
                    <Stack>
                      <Stack className=" gap-y-4">
                        <Stack>
                          <Label
                            required={!isReviewing}
                            isPreview={isReviewing}
                          >
                            {t("guestInfoPage.portraitLabel")}
                          </Label>
                          <Text
                            as="p"
                            className="-text--component-colors-field-default-description max-w-[400px]"
                          >
                            {t("guestInfoPage.imgValidation")}
                          </Text>
                          <Text
                            as="p"
                            className="-text--component-colors-field-default-description"
                          >
                            {t("guestInfoPage.photoRequirementsNote")}
                          </Text>
                        </Stack>
                        <Stack className="items-center">
                          {formErrors.portrait && (
                            <Stack className="-text--theme-colors-danger-default mb-5">
                              <Text>{t("guestInfoPage.photoIssueMSG")}</Text>
                              <Text>{formErrors.portrait}</Text>
                            </Stack>
                          )}
                          <div>
                            <DocumentViewer
                              src={
                                portrait.preview ||
                                (colorScheme === "light"
                                  ? portraitplaceholder
                                  : portraitplaceholderDark)
                              }
                              extension={portrait.extension}
                              alt={
                                portrait.preview
                                  ? t("guestInfoPage.portraitAlt")
                                  : t("guestInfoPage.portraitPlaceholderAlt")
                              }
                              className={classNames(
                                { "!p-10": !portrait.preview },
                                "w-52 h-52 object-cover -bg--theme-colors-surface-neutral-level03 -rounded-image-viewer",
                              )}
                            />
                          </div>
                          <div className="w-52 mt-5">
                            <HeadlessUploader
                              disabled={
                                EVENT === "SPORTICO"
                                  ? false
                                  : !conditions.instructions ||
                                    portrait.isLoading
                              }
                              formats={[".jpeg", ".jpg", ".png"]}
                              onUpload={async (file) => {
                                if (!file) return;

                                // Resize the image if needed
                                try {
                                  const processedFile =
                                    await resizeImageIfNeeded(file);

                                  // Create object URL from the processed image
                                  const objectUrl =
                                    URL.createObjectURL(processedFile);
                                  const extension = processedFile.name
                                    .split(".")
                                    .pop();

                                  setClientDocuments((prev) => ({
                                    ...prev,
                                    portrait: {
                                      ...prev.portrait,
                                      preview: objectUrl,
                                      value: processedFile, // Use the processed image
                                      state: "success",
                                      extension,
                                    },
                                  }));
                                } catch (error) {
                                  console.error(
                                    "Error processing image:",
                                    error,
                                  );
                                }
                              }}
                              className="h-[60px]"
                            >
                              <Button
                                variant="primary"
                                as="div"
                                size="lg"
                                fullWidth
                                disabled={
                                  EVENT === "SPORTICO"
                                    ? false
                                    : !conditions.instructions ||
                                      portrait.isLoading
                                }
                                isLoading={portrait.isLoading}
                              >
                                <div className="flex gap-x-2.5 justify-center items-center">
                                  <Text>
                                    {t("guestInfoPage.browsePhotoLabel")}
                                  </Text>
                                  <FontAwesomeIcon
                                    name="fa-cloud-arrow-up"
                                    size="lg"
                                  />
                                </div>
                              </Button>
                            </HeadlessUploader>
                          </div>
                          {portrait.state === "success" && (
                            // portraitErrors.length === 0 && (
                            <div className="flex gap-x-2.5 items-center mt-5 -text--theme-colors-success-default">
                              <span className="-text--theme-colors-success-default">
                                <FontAwesomeIcon
                                  name="fa-circle-check"
                                  size="2x"
                                />
                              </span>
                              <Text className="text-base">
                                {t("guestInfoPage.portraitUploadedMSG")}
                              </Text>
                            </div>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  )}
                  {sections["documents"]?.fields.passport_image && (
                    <Stack className="mt-10">
                      <Label required={!isReviewing}>
                        {t("guestInfoPage.passportLabel")}
                      </Label>
                      {formErrors.passport && (
                        <Stack className="-text--theme-colors-danger-default mb-5">
                          <Text>
                            {t("guestInfoPage.uploadedDocumentIssueLabel")}
                          </Text>
                          <Text>{formErrors.passport}</Text>
                        </Stack>
                      )}
                      {/* hide for now  */}
                      {/* <div
                          role="button"
                          className="text-sm -text--theme-colors-action-link-default-fg underline"
                          onClick={(e) => {
                            e.preventDefault();
                            openModal({
                              content: <PassportExampleModal />,
                            });
                          }}
                        >
                          Upload Passport (your data will be extracted
                          automatically)
                        </div> */}
                      <Stack className="mb-5">
                        <div className="flex flex-col md:flex-row md:gap-x-2.5 space-y-2.5 md:space-y-s0 mt-2.5">
                          <div className="relative w-full">
                            <FileInput
                              maxSize={40} // 40MB
                              buttonText={t("guestInfoPage.saveLabel")}
                              name="passport"
                              id="passport"
                              onChange={(e) => {
                                if (!e) return;
                                passportImageRef.current = [e.target.files[0]];
                                forceUpdate();
                              }}
                              disabled={
                                EVENT === "SPORTICO"
                                  ? false
                                  : !conditions.instructions ||
                                    passport.isLoading
                              }
                              onSave={async (e) => {
                                e.preventDefault();
                                if (!passportImageRef.current)
                                  return toast(
                                    t("guestInfoPage.noFileSelected"),
                                  );
                                const file = passportImageRef.current[0];
                                const extension = file.name.split(".").pop();
                                const objectUrl = URL.createObjectURL(file);
                                const processedFile =
                                  await resizeImageIfNeeded(file);
                                setClientDocuments((prev) => ({
                                  ...prev,
                                  passport: {
                                    ...prev.passport,
                                    preview: objectUrl,
                                    value: processedFile,
                                    state: "success",
                                    extension,
                                  },
                                }));
                              }}
                              placeholder={t(
                                "guestInfoPage.passportPlaceholder",
                              )}
                              formats={[".jpg", ".jpeg", ".png"]}
                            />
                          </div>
                        </div>
                        {passport.state === "success" && (
                          <div className="flex gap-x-2.5 items-center mt-2.5 -text--theme-colors-success-default">
                            <span className="-text--theme-colors-success-default">
                              <FontAwesomeIcon
                                name="fa-circle-check"
                                size="2x"
                              />
                            </span>
                            <Text className="text-bas">
                              {t("guestInfoPage.passportUploadedSuccessfully")}
                            </Text>
                          </div>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </>
              )}
            </>
            <Stack
              className={classNames("gap-y-10", {
                "opacity-20 pointer-events-none select-none cursor-not-allowed":
                  isFormDisabled,
              })}
            >
              {/* Documents Section - Dynamic Fields Only */}
              <FormSection
                sectionKey="documents"
                sections={documentsSection}
                showTitle={
                  sections["documents"]?.fields.image ||
                  sections["documents"]?.fields.passport_image
                    ? false
                    : true
                }
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                showDivider={
                  sections["documents"]?.fields.image &&
                  sections["documents"]?.fields.passport_image
                }
                customProps={{
                  defaultCountryCode: defaultCountryCode,
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              {/* Identification Info Section */}
              <FormSection
                sectionKey="identification_info"
                title={t("guestInfoPage.identificationInfoLabel")}
                sections={sections}
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                customProps={{
                  defaultCountryCode: defaultCountryCode,
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              {/* Personal Info Section */}
              <FormSection
                sectionKey="personal_info"
                title={t("guestInfoPage.PersonalInfo")}
                sections={sections}
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                customProps={{
                  customPrefixValue: customPrefix,
                  onCustomPrefixChange: handleCustomPrefixChange,
                  customPrefixError: formErrors.customPrefix,
                  defaultCountryCode: defaultCountryCode,
                  isCompanion: isCompanion,
                  nationality: dynamicFieldValues["nationality"],
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              {/* Address Info Section */}
              <FormSection
                sectionKey="address_info"
                sections={sections}
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                customProps={{
                  defaultCountryCode: defaultCountryCode,
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              {/* Contact Info Section */}
              <FormSection
                sectionKey="contact_info"
                title={t("guestInfoPage.contactInfoLabel")}
                sections={sections}
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                customProps={{
                  defaultCountryCode: defaultCountryCode,
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              {/* Other Info Section */}
              <FormSection
                sectionKey="other_info"
                sections={sections}
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                customProps={{
                  defaultCountryCode: defaultCountryCode,
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              <>
                {(flightOptionID == 1 || flightOptionID == 3) && (
                  <Stack
                    component="section"
                    onClick={() =>
                      isFormDisabled &&
                      toast(t("guestInfoPage.uploadDocsFirst"))
                    }
                  >
                    <Stack className="gap-y-4">
                      <Heading
                        as="h2"
                        className="-text--theme-colors-text-title-brand"
                      >
                        {t("guestInfoPage.flightInfoLabel")}
                      </Heading>
                      {isReviewing && conditions.willBookFlight && (
                        <Text className="-text--theme-colors-text-body text-xl">
                          -
                        </Text>
                      )}
                      <>
                        <Stack className="gap-y-8">
                          <>
                            {!isReviewing && (
                              <Stack>
                                <Text className="mb-3 -text--theme-colors-text-body text-xs font-normal">
                                  {
                                    EVENT_CONTENT.guestInfoPage
                                      .selfManagingFlightDescription
                                  }
                                </Text>
                                <Text className="mb-3 -text--theme-colors-text-body text-xs font-normal">
                                  {
                                    EVENT_CONTENT.guestInfoPage
                                      .selfManagingFlightDescription2
                                  }
                                </Text>
                                <Text className="mb-3 -text--theme-colors-text-body text-xs font-normal">
                                  {
                                    EVENT_CONTENT.guestInfoPage
                                      .selfManagingFlightDescription3
                                  }
                                </Text>
                                <div className="flex items-start gap-x-2.5">
                                  <Label
                                    htmlFor="will-book-flight"
                                    className="-text--component-colors-check-radio-default-text text-sm font-normal flex"
                                  >
                                    <Checkbox
                                      className="me-2"
                                      id="will-book-flight"
                                      name="will-book-flight"
                                      checked={conditions.willBookFlight}
                                      isPreview={isReviewing}
                                      onChange={(e) => {
                                        const newWillBookFlight =
                                          !conditions.willBookFlight;
                                        setConditions({
                                          ...conditions,
                                          willBookFlight: newWillBookFlight,
                                        });

                                        handleAttendeeDriveFlightBooking(
                                          newWillBookFlight,
                                        );

                                        // Clear flight-related errors when user chooses to book their own flight
                                        if (newWillBookFlight) {
                                          setFormErrors((prev) => ({
                                            ...prev,
                                            outbound_departure_airport_code: "",
                                            outbound_arrival_airport_code: "",
                                            return_departure_airport_code: "",
                                            return_arrival_airport_code: "",
                                          }));
                                        }
                                      }}
                                    />
                                    {
                                      EVENT_CONTENT.guestInfoPage
                                        .selfManagingFlight
                                    }
                                  </Label>
                                </div>
                              </Stack>
                            )}
                            {conditions.willBookFlight !== true &&
                              flightOptionID == 1 && (
                                <Stack className="gap-y-[5px]">
                                  <Text className="-text--theme-colors-text-subtitle-brand text-sm font-normal cursor-default">
                                    {t("guestInfoPage.flightTitle")}
                                  </Text>
                                  <Text className="-text--theme-colors-text-body text-[20px]">
                                    PR 001
                                  </Text>
                                  {flightOptionID !== 1 &&
                                    flightOptionID !== 0 && (
                                      <Text className="-text--component-colors-field-filled-description text-sm">
                                        {t(
                                          "guestInfoPage.uploadYourTravelDetails",
                                        )}
                                      </Text>
                                    )}
                                </Stack>
                              )}
                          </>
                          {flightOptionID === 1 &&
                            conditions.willBookFlight !== true && (
                              <Stack className="gap-y-10">
                                <Stack className="gap-y-2.5">
                                  <Text
                                    className="-text--theme-colors-text-subtitle-brand text-sm font-normal cursor-default"
                                    isPreview={isReviewing}
                                  >
                                    {t("guestInfoPage.outboundFlight")}
                                  </Text>

                                  <FieldRow>
                                    <FieldSet>
                                      <Input
                                        label={t("guestInfoPage.fromLabel")}
                                        value={
                                          flightDetails.outbound_departure_airport_code
                                        }
                                        disabled
                                        locked
                                        isPreview={isReviewing}
                                      />
                                    </FieldSet>

                                    <FieldSet>
                                      <Input
                                        label={t("guestInfoPage.toLabel")}
                                        value={
                                          flightDetails.outbound_arrival_airport_code
                                        }
                                        disabled
                                        locked
                                        isPreview={isReviewing}
                                      />
                                    </FieldSet>
                                  </FieldRow>
                                </Stack>
                                <Stack className="gap-y-2.5">
                                  <Text
                                    className="-text--theme-colors-text-subtitle-brand text-sm font-normal cursor-default"
                                    isPreview={isReviewing}
                                  >
                                    {t("guestInfoPage.returnFlightLabel")}
                                  </Text>

                                  <FieldRow>
                                    <FieldSet>
                                      <Input
                                        label={t("guestInfoPage.fromLabel")}
                                        value={
                                          flightDetails.outbound_arrival_airport_code
                                        }
                                        disabled
                                        locked
                                        isPreview={isReviewing}
                                      />
                                    </FieldSet>
                                    <FieldSet>
                                      <Input
                                        label={t("guestInfoPage.toLabel")}
                                        value={
                                          flightDetails.outbound_departure_airport_code
                                        }
                                        disabled
                                        locked
                                        isPreview={isReviewing}
                                      />
                                    </FieldSet>
                                  </FieldRow>
                                </Stack>
                              </Stack>
                            )}
                        </Stack>
                        {flightOptionID === 3 &&
                          conditions.willBookFlight !== true && (
                            <Stack className="space-y-s20 mb-s20">
                              <Stack>
                                <Text
                                  className="-text--theme-colors-text-subtitle-brand text-sm font-normal cursor-default"
                                  isPreview={isReviewing}
                                >
                                  {t("guestInfoPage.outboundFlight")}
                                </Text>
                                <FieldRow>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t("guestInfoPage.fromLabel")}
                                        isPreview
                                        value={
                                          flightDetails.outbound_departure_airport_name
                                        }
                                      />
                                    ) : (
                                      <SearchableSelect
                                        label={t("guestInfoPage.fromLabel")}
                                        disabled={isFormDisabled}
                                        options={
                                          tripOptions
                                            .outbound_departure_airport_code
                                            ?.options
                                        }
                                        isLoading={
                                          tripOptions
                                            .outbound_departure_airport_code
                                            ?.isLoading
                                        }
                                        value={
                                          flightDetails.outbound_departure_airport_code
                                        }
                                        onSearch={(search) =>
                                          searchAirports(
                                            search,
                                            "outbound_departure_airport_code",
                                          )
                                        }
                                        onChange={(option) => {
                                          setFormErrors((prev) => ({
                                            ...prev,
                                            outbound_departure_airport_code: "",
                                          }));
                                          setFlightDetails((prev) => ({
                                            ...prev,
                                            outbound_departure_airport_name:
                                              getAirportLabel(option),
                                            outbound_departure_airport_code:
                                              option.value,
                                          }));
                                        }}
                                      />
                                    )}
                                  </FieldSet>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t("guestInfoPage.toLabel")}
                                        isPreview
                                        value={
                                          flightDetails.outbound_arrival_airport_name
                                        }
                                      />
                                    ) : (
                                      <SearchableSelect
                                        label={t("guestInfoPage.toLabel")}
                                        disabled={isFormDisabled}
                                        options={
                                          tripOptions
                                            .outbound_arrival_airport_code
                                            ?.options
                                        }
                                        isLoading={
                                          tripOptions
                                            .outbound_arrival_airport_code
                                            ?.isLoading
                                        }
                                        value={
                                          flightDetails.outbound_arrival_airport_code
                                        }
                                        onSearch={(search) =>
                                          searchAirports(
                                            search,
                                            "outbound_arrival_airport_code",
                                          )
                                        }
                                        onChange={(option) => {
                                          setFormErrors((prev) => ({
                                            ...prev,
                                            outbound_arrival_airport_code: "",
                                          }));
                                          setFlightDetails((prev) => ({
                                            ...prev,
                                            outbound_arrival_airport_name:
                                              getAirportLabel(option),
                                            outbound_arrival_airport_code:
                                              option.value,
                                          }));
                                        }}
                                      />
                                    )}
                                  </FieldSet>
                                </FieldRow>
                              </Stack>
                              <Stack>
                                <FieldRow>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t(
                                          "guestInfoPage.flightPrefOutboundTime",
                                        )}
                                        isPreview
                                        value={
                                          selectedPref.outboundSelectedPrefTime
                                        }
                                      />
                                    ) : (
                                      <Stack className="gap-y-2">
                                        <Label isPreview={isReviewing}>
                                          {t(
                                            "guestInfoPage.flightPrefOutboundTime",
                                          )}
                                        </Label>
                                        <Select
                                          options={preferredTimes}
                                          value={
                                            selectedPref.outboundSelectedPrefTime
                                          }
                                          isPreview={isReviewing}
                                          onChange={(e) => {
                                            setSelectedPref((prev) => ({
                                              ...prev,
                                              outboundSelectedPrefTime:
                                                e.target.value,
                                            }));
                                          }}
                                        />
                                      </Stack>
                                    )}
                                  </FieldSet>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t(
                                          "guestInfoPage.flightPrefOutboundDate",
                                        )}
                                        isPreview
                                        value={
                                          selectedPref.outboundSelectedPrefDate
                                        }
                                      />
                                    ) : (
                                      <Stack className="gap-y-2">
                                        <Label isPreview={isReviewing}>
                                          {t(
                                            "guestInfoPage.flightPrefOutboundDate",
                                          )}
                                        </Label>
                                        <Input
                                          type="date"
                                          isPreview={isReviewing}
                                          min={
                                            EVENT === "SPORTICO"
                                              ? "2024-11-01"
                                              : ""
                                          }
                                          value={
                                            selectedPref.outboundSelectedPrefDate
                                          }
                                          onChange={(e) => {
                                            setSelectedPref((prev) => ({
                                              ...prev,
                                              outboundSelectedPrefDate:
                                                e.target.value,
                                            }));
                                          }}
                                        />
                                      </Stack>
                                    )}
                                  </FieldSet>
                                </FieldRow>
                              </Stack>
                              <Stack>
                                <Text
                                  className="-text--theme-colors-text-subtitle-brand text-sm font-normal cursor-default"
                                  isPreview={isReviewing}
                                >
                                  {t("guestInfoPage.returnFlightLabel")}
                                </Text>
                                <FieldRow>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t("guestInfoPage.fromLabel")}
                                        isPreview
                                        value={
                                          flightDetails.return_departure_airport_name
                                        }
                                      />
                                    ) : (
                                      <SearchableSelect
                                        label={t("guestInfoPage.fromLabel")}
                                        options={
                                          tripOptions
                                            .return_departure_airport_code
                                            ?.options
                                        }
                                        disabled={isFormDisabled}
                                        isLoading={
                                          tripOptions
                                            .return_departure_airport_code
                                            ?.isLoading
                                        }
                                        value={
                                          flightDetails.return_departure_airport_code
                                        }
                                        onSearch={(search) =>
                                          searchAirports(
                                            search,
                                            "return_departure_airport_code",
                                          )
                                        }
                                        onChange={(option) => {
                                          setFormErrors((prev) => ({
                                            ...prev,
                                            return_departure_airport_code: "",
                                          }));
                                          setFlightDetails((prev) => ({
                                            ...prev,
                                            return_departure_airport_name:
                                              getAirportLabel(option),
                                            return_departure_airport_code:
                                              option.value,
                                          }));
                                        }}
                                      />
                                    )}
                                  </FieldSet>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t("guestInfoPage.toLabel")}
                                        isPreview
                                        value={
                                          flightDetails.return_arrival_airport_name
                                        }
                                      />
                                    ) : (
                                      <SearchableSelect
                                        label={t("guestInfoPage.toLabel")}
                                        options={
                                          tripOptions
                                            .return_arrival_airport_code
                                            ?.options
                                        }
                                        disabled={isFormDisabled}
                                        isLoading={
                                          tripOptions
                                            .return_arrival_airport_code
                                            ?.isLoading
                                        }
                                        value={
                                          flightDetails.return_arrival_airport_code
                                        }
                                        onSearch={(search) =>
                                          searchAirports(
                                            search,
                                            "return_arrival_airport_code",
                                          )
                                        }
                                        onChange={(option) => {
                                          setFormErrors((prev) => ({
                                            ...prev,
                                            return_arrival_airport_code: "",
                                          }));
                                          setFlightDetails((prev) => ({
                                            ...prev,
                                            return_arrival_airport_name:
                                              getAirportLabel(option),
                                            return_arrival_airport_code:
                                              option.value,
                                          }));
                                        }}
                                      />
                                    )}
                                  </FieldSet>
                                </FieldRow>
                              </Stack>
                              <Stack>
                                <FieldRow>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t(
                                          "guestInfoPage.flightPrefReturnTime",
                                        )}
                                        isPreview
                                        value={
                                          selectedPref.returnSelectedPrefTime
                                        }
                                      />
                                    ) : (
                                      <Stack className="gap-y-2">
                                        <Label isPreview={isReviewing}>
                                          {t(
                                            "guestInfoPage.flightPrefReturnTime",
                                          )}
                                        </Label>
                                        <Select
                                          options={preferredTimes}
                                          value={
                                            selectedPref.returnSelectedPrefTime
                                          }
                                          isPreview={isReviewing}
                                          onChange={(e) => {
                                            setSelectedPref((prev) => ({
                                              ...prev,
                                              returnSelectedPrefTime:
                                                e.target.value,
                                            }));
                                          }}
                                        />
                                      </Stack>
                                    )}
                                  </FieldSet>
                                  <FieldSet>
                                    {isReviewing ? (
                                      <Input
                                        label={t(
                                          "guestInfoPage.flightPrefReturnDate",
                                        )}
                                        isPreview
                                        value={
                                          selectedPref.returnSelectedPrefDate
                                        }
                                      />
                                    ) : (
                                      <Stack className="gap-y-2">
                                        <Label isPreview={isReviewing}>
                                          {t(
                                            "guestInfoPage.flightPrefReturnDate",
                                          )}
                                        </Label>
                                        <Input
                                          type="date"
                                          isPreview={isReviewing}
                                          min={
                                            EVENT === "SPORTICO"
                                              ? "2024-11-01"
                                              : ""
                                          }
                                          value={
                                            selectedPref.returnSelectedPrefDate
                                          }
                                          onChange={(e) => {
                                            setSelectedPref((prev) => ({
                                              ...prev,
                                              returnSelectedPrefDate:
                                                e.target.value,
                                            }));
                                          }}
                                        />
                                      </Stack>
                                    )}
                                  </FieldSet>
                                </FieldRow>
                              </Stack>
                            </Stack>
                          )}
                        {conditions.willBookFlight !== true && (
                          <>
                            {(flightOptionID === 1 || flightOptionID === 3) && (
                              <Stack className="gap-y-2.5">
                                <Input
                                  label={t("guestInfoPage.flightConcerns")}
                                  type="textarea"
                                  rows={5}
                                  value={flightNotes}
                                  onChange={(e) =>
                                    setFlightNotes(e.target.value)
                                  }
                                  disabled={isFormDisabled}
                                  isPreview={isReviewing}
                                />
                                {!isReviewing && (
                                  <Text className="text-sm -text--component-colors-field-default-description">
                                    {t("guestInfoPage.concernsInstructions")}
                                  </Text>
                                )}
                              </Stack>
                            )}
                          </>
                        )}
                      </>
                    </Stack>
                  </Stack>
                )}
              </>

              {/* Travel Documents Section */}
              <FormSection
                sectionKey="travel_documents"
                title={t("guestInfoPage.travelDocumentsStatus")}
                sections={sections}
                sectionsData={sectionsData}
                dynamicFieldValues={dynamicFieldValues}
                dynamicFieldErrors={dynamicFieldErrors}
                updateDynamicField={handleDynamicFieldChange}
                isReviewing={isReviewing}
                isFormDisabled={isFormDisabled}
                onDisabledClick={onDisabledClick}
                showDivider={false}
                customProps={{
                  defaultCountryCode: defaultCountryCode,
                  countryCode: countryCode,
                  emergencyCountryCode: emergencyCountryCode,
                  assistantCountryCode: assistantCountryCode,
                  setCountryCode: setCountryCode,
                  setEmergencyCountryCode: setEmergencyCountryCode,
                  setAssistantCountryCode: setAssistantCountryCode,
                }}
              />

              <Stack>
                {isReviewing ? (
                  <>
                    <Stack>
                      <div className="flex items-start gap-x-2.5 mb-2">
                        <Label
                          htmlFor="privacy-check"
                          className="-text--component-colors-check-radio-default-text text-sm font-normal flex"
                        >
                          <Checkbox
                            className="me-2"
                            id="privacy-check"
                            name="privacy-check"
                            checked={privacyCheck}
                            onChange={(e) => {
                              setPrivacyCheck(e.target.checked);
                            }}
                          />
                          {t("guestDetails.privacyPolicy.checkboxContent")}
                        </Label>
                      </div>
                      <div className="mb-2 mt-2 flex flex-col -text--theme-colors-text-body space-y-0 text-xs">
                        <p className="flex flex-wrap items-center gap-x-1 gap-y-2 w-2xl">
                          <Text as="span">
                            {t(
                              "guestDetails.privacyPolicy.privacyPolicyBeforeSubmit",
                            )}
                          </Text>
                          <Text as="span">
                            {t(
                              "guestDetails.privacyPolicy.privacyPolicyBeforeSubmit2",
                            )}
                          </Text>
                          <Button
                            variant="link"
                            type="button"
                            onClick={() =>
                              openModal({
                                content: (
                                  <TermsOfUseAndPolicyModal
                                    type="policy"
                                    closeModal={closeModal}
                                  />
                                ),
                              })
                            }
                            size={"md"}
                            className="font-normal whitespace-nowrap -text--theme-colors-text-body text-base"
                          >
                            {t(
                              "guestDetails.privacyPolicy.securityPolicyLabel",
                            )}
                          </Button>
                        </p>
                      </div>
                    </Stack>
                    <Text
                      as="span"
                      className="text-xs mb-10 -text--theme-colors-text-body"
                    >
                      {t("guestDetails.privacyPolicy.ensureBeforeSubmitLabel")}
                    </Text>
                    <div className="flex flex-col md:flex-row items-center gap-y-4 gap-x-8">
                      <Button
                        fullWidth
                        variant="secondary"
                        onClick={(e) => {
                          pushToDataLayer({
                            event: "back_to_edit",
                            screen_name: "attendance review",
                            journey_step: "attendance form",
                            action: "target_attendance form",
                          });
                          e.preventDefault();
                          setIsReviewing(false);
                        }}
                      >
                        {t("guestInfoPage.backToEditLabel")}
                      </Button>
                      <Button
                        fullWidth
                        isLoading={isLoading}
                        variant="primary"
                        onClick={(e) => {
                          pushToDataLayer({
                            event: "submit_attendance_form",
                            screen_name: "attendance form",
                            journey_step: "attendance form",
                          });
                          handleOnSubmit(e);
                        }}
                        disabled={!privacyCheck}
                      >
                        {t("guestInfoPage.submitLabel")}
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    disabled={
                      EVENT === "SPORTICO"
                        ? false
                        : !conditions.instructions || isFormDisabled
                    }
                    isLoading={isLoading}
                    onClick={(e) => {
                      const isValid = validateForm();
                      if (isValid) {
                        pushToDataLayer({
                          event: "save_attendance_form",
                          screen_name: "attendance form",
                          journey_step: "attendance form",
                        });
                        setIsReviewing(true);
                        window.scrollTo({
                          top: 0,
                          behavior: "instant",
                        });
                      } else {
                        scrollToError();
                      }
                    }}
                  >
                    <div className="flex items-center justify-center gap-x-s10">
                      <Text as="span">{t("guestInfoPage.saveLabel")}</Text>
                      <FontAwesomeIcon name="fa-chevron-right" />
                    </div>
                  </Button>
                )}
                {/* {conditions.willBookFlight && (
                  <Text className="text-font-primary mt-2 text-sm">
                    *You will be responsible for uploading your flight
                    confirmation on this dashboard once your trip is confirmed
                  </Text>
                )} */}
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <div className="flex items-center justify-center">
            <LoadingDots />
          </div>
        )}
      </Stack>
    </PageLayout>
  );
};
export default PersonalInfo;
